import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, ParamMap, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppBarTitleService, parseNumber } from 'common';
import { BrokerStatus, BrokerStatusLabel } from '@partner/shared/SharedConstants';
import { SubmissionDetailsFacade } from './submission-details.facade';
import { SubmissionDetailsHandleSharedActionsService } from './actions/submission-details-handle-shared-actions.service';
import { SubmissionDetailsActionsService } from './actions/submission-details-actions.service';
import { filter, startWith, switchMap } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { SubmissionDetailsParams } from './submission-details.model';
import { CanDeactivateSubmissionDetails } from '../../infrastructure/guards/can-deactivate-submission-details.guard';

@UntilDestroy()
@Component({
    selector: 'ifbp-submission-details',
    templateUrl: './submission-details.component.html',
    styleUrls: ['./submission-details.component.scss'],
    standalone: false
})
export class SubmissionDetailsComponent implements OnInit, CanDeactivateSubmissionDetails, OnDestroy {
  params: SubmissionDetailsParams;
  brokerStatus: BrokerStatus;
  isDataLoaded: boolean;

  readonly BrokerStatus = BrokerStatus;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly facade: SubmissionDetailsFacade,
    private readonly actions: SubmissionDetailsActionsService,
    private readonly handleSharedActions: SubmissionDetailsHandleSharedActionsService,
  ) { }

  ngOnInit(): void {
    this.initData();
    this.initActions();
  }

  private initData(): void {
    const routerEvent$ = this.router.events.pipe(filter((event) => event instanceof NavigationEnd), startWith(this.router));
    const paramMap$ = this.route.paramMap;
    combineLatest([routerEvent$, paramMap$]).pipe(
      untilDestroyed(this),
      switchMap(([_, paramMap]: [any, ParamMap]) => {
        this.params = { appId: parseNumber(paramMap.get("id")) };
        return this.facade.getComponentBaseData$(this.params.appId)
          .pipe(untilDestroyed(this))
      })).subscribe(data => {
        this.brokerStatus = data.application.brokerStatus;
        this.actions.updateSubmissionDetailsAppBarTitle({
          title: `${data.account?.name ?? 'N/A'} - ${BrokerStatusLabel.get(data.application.brokerStatus)}`
        });
      });
  }

  private initActions(): void {
  this.handleSharedActions.watchCommonActions().pipe(untilDestroyed(this)).subscribe();
  this.actions.markDataAsLoaded$.pipe(untilDestroyed(this)).subscribe(() => {
      this.isDataLoaded = true;
    });
  }

  canDeactivate(): Observable<boolean> {
    if (this.brokerStatus === BrokerStatus.DRAFT) {
      this.actions.canDeactivateRequest();
      return this.actions.canDeactivate();
    }
    return of(true);
  }

  ngOnDestroy(): void {
    this.facade.clearSubmissionCache();
  }
}
